/**
 * 案件の新規登録の下書き (docs/features/manual-product-brand-first-flow.md §6.7「登録時に作る」)。
 *
 * - 「登録する」を押すまで案件行は作らない。文字項目・画像 (ストレージへ上げた URL)・ブランド選択・採用した読み取り結果を
 *   クライアントの下書き (ProductDraft) に持ち、1 回の POST /products で作る
 * - 下書きは draftId (uuid) で識別する。入口画面を開いた時点で発行し、フォーム・CreateBrandModal・読み取りジョブの
 *   復帰キー (urlSummaryResumeKey の productId の位置) に同じ値を使う。複数の下書き (別タブ・ページと素材モーダル) が同時に
 *   あっても混ざらない
 * - 復帰: フォームの内容は sessionStorage に `userUid:organizationId:draftId` で持ち、同じタブのリロード・戻るから戻す。
 *   保存データにも持ち主 (userUid / organizationId) を入れて復元時に照合し、別ユーザー・別組織では復元しない。
 *   登録成功・明示キャンセルで消す。単なる離脱では残す (タブを閉じれば sessionStorage ごと消える)
 */
import type { IndustryCategory } from './industry';
import type { CreateProductRequest, ProductAdditionalImage } from './types';
import { mergeAttributes, type SourcePages } from './urlSummary';

export interface ProductDraftForm {
  title: string;
  description: string;
  sku: string;
  barcode_value: string;
  brand_id: string;
  brand_name: string;
  product_type: string;
  category: string;
  industry_categories: IndustryCategory[];
  compliance_defaults: Record<string, boolean>;
}

/** 画像は案件行と同じ形で持つ (buildManualSlots がそのまま枠に組める)。url が空の要素は空き枠 */
export interface ProductDraftImages {
  main_image_url: string;
  main_image_name: string | null;
  main_image_original_path: string | null;
  additional_images: ProductAdditionalImage[];
}

export interface ProductDraft {
  draftId: string;
  form: ProductDraftForm;
  images: ProductDraftImages;
  /** URL 読み取りで採用した補足属性 (登録時に attributes として送る) */
  attributesPatch: Record<string, string>;
  /** URL 読み取りの取得元 (attributes.source_pages) */
  sourcePages: SourcePages | null;
}

export const EMPTY_DRAFT_FORM: ProductDraftForm = {
  title: '',
  description: '',
  sku: '',
  barcode_value: '',
  brand_id: '',
  brand_name: '',
  product_type: '',
  category: '',
  industry_categories: [],
  compliance_defaults: {},
};

export const EMPTY_DRAFT_IMAGES: ProductDraftImages = {
  main_image_url: '',
  main_image_name: null,
  main_image_original_path: null,
  additional_images: [],
};

export function newDraftId(): string {
  const c = typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // randomUUID が無い環境 (古い WebView 等) の代替。衝突の確率は実用上無視できる
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function emptyProductDraft(draftId: string, form: Partial<ProductDraftForm> = {}): ProductDraft {
  return {
    draftId,
    form: { ...EMPTY_DRAFT_FORM, ...form },
    images: { ...EMPTY_DRAFT_IMAGES, additional_images: [] },
    attributesPatch: {},
    sourcePages: null,
  };
}

/** 何も入力されていない (捨てても失うものがない) */
export function isProductDraftEmpty(draft: ProductDraft): boolean {
  const f = draft.form;
  const textEmpty = [f.title, f.description, f.sku, f.barcode_value, f.brand_id, f.brand_name, f.product_type, f.category].every((v) => !v.trim());
  return (
    textEmpty
    && f.industry_categories.length === 0
    && Object.keys(f.compliance_defaults).length === 0
    && !draft.images.main_image_url.trim()
    && !draft.images.additional_images.some((img) => !!img.url?.trim())
    && Object.keys(draft.attributesPatch).length === 0
  );
}

/** 「登録する」で送る本文。空文字の項目は送らない (backend は None を除いて保存する) */
export function draftToCreateRequest(draft: ProductDraft): CreateProductRequest {
  const f = draft.form;
  const data: CreateProductRequest = { title: f.title.trim() };
  if (f.description.trim()) data.description = f.description;
  if (f.brand_name.trim()) data.brand_name = f.brand_name.trim();
  if (f.brand_id) data.brand_id = f.brand_id;
  if (f.product_type.trim()) data.product_type = f.product_type.trim();
  if (f.category.trim()) data.category = f.category;
  if (f.sku.trim()) data.sku = f.sku.trim();
  if (f.barcode_value.trim()) data.barcode_value = f.barcode_value.trim();
  if (f.industry_categories.length > 0) data.industry_categories = f.industry_categories;
  if (Object.keys(f.compliance_defaults).length > 0) data.compliance_defaults = f.compliance_defaults;
  const images = draft.images;
  if (images.main_image_url.trim()) {
    data.main_image_url = images.main_image_url.trim();
    if (images.main_image_name) data.main_image_name = images.main_image_name;
    if (images.main_image_original_path) data.main_image_original_path = images.main_image_original_path;
  }
  // 末尾の空き枠は落とし、途中の空き枠 ({url: ''}) は位置を保つ
  let lastFilled = -1;
  images.additional_images.forEach((img, i) => { if (img.url?.trim()) lastFilled = i; });
  if (lastFilled >= 0) {
    data.additional_images = images.additional_images.slice(0, lastFilled + 1).map((img) => (img.url?.trim() ? img : { url: '' }));
  }
  if (Object.keys(draft.attributesPatch).length > 0 || draft.sourcePages) {
    data.attributes = mergeAttributes(draft.attributesPatch, {});
    if (draft.sourcePages) data.attributes.source_pages = draft.sourcePages;
  }
  return data;
}

// ============================================================
// sessionStorage への保存 (同じタブでのリロード・戻るから復元する)
// ============================================================

export interface ProductDraftScope {
  userUid: string;
  organizationId: string | null;
}

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface StoredProductDraft {
  v: 1;
  scope: ProductDraftScope;
  savedAt: number;
  draft: ProductDraft;
}

const DRAFT_KEY_PREFIX = 'productDraft:';

function draftStorage(): DraftStorage | null {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function productDraftKey(scope: ProductDraftScope, draftId: string): string {
  return `${DRAFT_KEY_PREFIX}${scope.userUid}:${scope.organizationId || '-'}:${draftId}`;
}

export function saveProductDraft(scope: ProductDraftScope, draft: ProductDraft, storage: DraftStorage | null = draftStorage()): void {
  try {
    const stored: StoredProductDraft = { v: 1, scope, savedAt: Date.now(), draft };
    storage?.setItem(productDraftKey(scope, draft.draftId), JSON.stringify(stored));
  } catch {
    // 保存できなくても入力自体は続けられる (復元だけ利かない)
  }
}

/** 持ち主 (userUid / organizationId) と draftId が一致するものだけ返す。形が崩れていれば null */
export function loadProductDraft(scope: ProductDraftScope, draftId: string, storage: DraftStorage | null = draftStorage()): ProductDraft | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(productDraftKey(scope, draftId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredProductDraft>;
    if (parsed.v !== 1 || !parsed.scope || !parsed.draft) return null;
    if (parsed.scope.userUid !== scope.userUid || (parsed.scope.organizationId || null) !== (scope.organizationId || null)) return null;
    const draft = parsed.draft;
    if (draft.draftId !== draftId || typeof draft.form !== 'object' || typeof draft.images !== 'object') return null;
    return {
      draftId,
      form: { ...EMPTY_DRAFT_FORM, ...draft.form, industry_categories: Array.isArray(draft.form.industry_categories) ? draft.form.industry_categories : [], compliance_defaults: draft.form.compliance_defaults && typeof draft.form.compliance_defaults === 'object' ? draft.form.compliance_defaults : {} },
      images: { ...EMPTY_DRAFT_IMAGES, ...draft.images, additional_images: Array.isArray(draft.images.additional_images) ? draft.images.additional_images : [] },
      attributesPatch: draft.attributesPatch && typeof draft.attributesPatch === 'object' ? draft.attributesPatch : {},
      sourcePages: draft.sourcePages ?? null,
    };
  } catch {
    return null;
  }
}

export function clearProductDraft(scope: ProductDraftScope, draftId: string, storage: DraftStorage | null = draftStorage()): void {
  try {
    storage?.removeItem(productDraftKey(scope, draftId));
  } catch {
    // 何もしない
  }
}
